import { CurriculumMappingQuizTaskDto } from '~/curriculum/types/mapping-quiz.types'
import { normalizeCountryCode } from '~/curriculum/utils/helpers'
import { homApi } from '~/redux/apis/hom'
import {
  DetailedUserStatsResponse,
  SubtopicTimesResponse,
  UserTimeResponse,
} from '~/statistics-v2/types/statistics.types'
import {
  StudentAllMathStatsResponse,
  StudentCurrentGradeResponse,
  StudentCurriculumCountryResponse,
  StudentGameStatsResponse,
  StudentProfileResponse,
  StudentQuizTaskArgs,
  StudentStatsArgs,
  StudentTimeArgs,
} from '~/teacher-panel/types/studentStats.types'

/**
 * The teacher's read of ONE pupil's statistics.
 *
 * These are the school-edition replacements for three b2c endpoints the school build must not call:
 * `/progress/get-user-stats?userId=`, `/progress/get-user-time?userId=` and
 * `/progress/get-recent-curriculum?familyMemberId=`. None of those three checks whether the caller may
 * read the id it was handed, so hom-api 403s all of them when `APP_EDITION=b2s`. The endpoints below
 * take the pupil in the PATH and hom-api verifies the caller actually teaches them.
 *
 * ⚠ **No `transformResponse` anywhere in this file, and do not add one.** These answer bare bodies with
 * no `{ data }` envelope, matching `teacher-panel.api.ts`. Unwrapping `.data` returns `undefined` on
 * every success — a screen of zeros that looks like a pupil who has done nothing.
 *
 * ⚠ The payloads are byte-identical to their b2c twins, which is the point — the screen renders with
 * `~/statistics-v2/components/*` unchanged. If a school-specific field is ever wanted, add an endpoint;
 * do not reshape these.
 */
export const studentStatsApi = homApi.injectEndpoints({
  overrideExisting: false,
  endpoints: (builder) => ({
    getStudentStats: builder.query<DetailedUserStatsResponse, StudentStatsArgs>({
      query: ({ studentId, countryCode }) => ({
        url: `/v1/school/teacher/students/${studentId}/stats`,
        method: 'GET',
        params: { countryCode },
      }),
    }),

    /**
     * The header's time figures, and the topic drill-down's. `gradeId` / `topicOrder` / `subtopicLevel`
     * narrow it; all absent means the whole country's curriculum.
     *
     * ⚠ Built through `params` rather than string concatenation because `fetchBaseQuery` strips
     * `undefined` from it. Concatenating would send the literal `?gradeId=undefined`, which the Joi
     * query schema rejects — a 400 on a request that meant "no filter".
     */
    getStudentTime: builder.query<UserTimeResponse, StudentTimeArgs>({
      query: ({ studentId, ...params }) => ({
        url: `/v1/school/teacher/students/${studentId}/time`,
        method: 'GET',
        params,
      }),
    }),

    /**
     * Every subtopic's time for the pupil, in one call — the subtopic cards in the drill-down.
     *
     * The b2c twin reads `?userId=` behind a family-admin check; here the pupil is in the PATH and
     * hom-api verifies the caller teaches them. No query parameters: the response keys carry the
     * country and grade.
     */
    getStudentSubtopicTimes: builder.query<SubtopicTimesResponse, { studentId: number }>({
      query: ({ studentId }) => ({
        url: `/v1/school/teacher/students/${studentId}/subtopic-times`,
        method: 'GET',
      }),
    }),

    /**
     * The pupil's Must-Know tasks for one topic, which scope the drill-down's Must-Knows per subtopic.
     * The b2c `/curriculum/quiz-task` reads the token holder, so here it answered about the TEACHER.
     * `normalizeCountryCode` as the b2c twin does — quiz rows are stored under the normalized code.
     */
    getStudentQuizTask: builder.query<CurriculumMappingQuizTaskDto, StudentQuizTaskArgs>({
      query: ({ studentId, ...params }) => ({
        url: `/v1/school/teacher/students/${studentId}/quiz-task`,
        method: 'GET',
        params: normalizeCountryCode(params),
      }),
    }),

    /**
     * Where the pupil is now — grade, topic and subtopic — so the drill-down opens on the subtopic they
     * are on. The b2c `/curriculum/current-grade` answers about the token holder.
     */
    getStudentCurrentGrade: builder.query<StudentCurrentGradeResponse, { studentId: number }>({
      query: ({ studentId }) => ({
        url: `/v1/school/teacher/students/${studentId}/current-grade`,
        method: 'GET',
      }),
    }),

    /** Which curriculum to render the page in — the pupil's, not the signed-in teacher's. */
    getStudentCurriculumCountry: builder.query<StudentCurriculumCountryResponse, { studentId: number }>({
      query: ({ studentId }) => ({
        url: `/v1/school/teacher/students/${studentId}/curriculum-country`,
        method: 'GET',
      }),
    }),

    /**
     * The pupil's nickname, avatar, XP and league — what the header needs above the figures.
     *
     * ⚠ Composed server-side, not forwarded like the three above. `leagueRank` is a number, never the
     * league's `users` array: a league pools pupils from every school on the platform.
     */
    getStudentProfile: builder.query<StudentProfileResponse, { studentId: number }>({
      query: ({ studentId }) => ({
        url: `/v1/school/teacher/students/${studentId}/profile`,
        method: 'GET',
      }),
    }),

    /**
     * Whole-portal totals for the All math selection — everything the pupil has done, not just the
     * curriculum.
     *
     * Replaces `/user-stats/time-spent`, `/user-stats/learning` and
     * `/user-stats/unmapped-activity-stats`, each of which answers about the signed-in user unless
     * given a `familyMemberId` a teacher cannot supply.
     *
     * ⚠ XP is not here — `getStudentProfile` already answers it from the same source.
     */
    getStudentAllMathStats: builder.query<StudentAllMathStatsResponse, { studentId: number }>({
      query: ({ studentId }) => ({
        url: `/v1/school/teacher/students/${studentId}/all-math-stats`,
        method: 'GET',
      }),
    }),

    /**
     * The pupil's four game cards, for All math. Replaces `/user-stats/games`, which takes its
     * subject from the session. Lifetime — the b2c date range is ignored server-side anyway.
     */
    getStudentGameStats: builder.query<StudentGameStatsResponse, { studentId: number }>({
      query: ({ studentId }) => ({
        url: `/v1/school/teacher/students/${studentId}/game-stats`,
        method: 'GET',
      }),
    }),
  }),
})

export const {
  useGetStudentAllMathStatsQuery,
  useGetStudentCurrentGradeQuery,
  useGetStudentCurriculumCountryQuery,
  useGetStudentGameStatsQuery,
  useGetStudentProfileQuery,
  useGetStudentQuizTaskQuery,
  useGetStudentStatsQuery,
  useGetStudentSubtopicTimesQuery,
  useGetStudentTimeQuery,
} = studentStatsApi
